import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import type { CurrencyTable } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';

/**
 * The minor-unit digits of a currency — BILLING's table, never the browser's `Intl` (money review C10, 2026-10-04).
 *
 * `Intl` disagrees with billing for several codes (IQD: Intl 0, billing/ISO 4217 3), and every console amount is billing's
 * integer minor units: formatting IQD 1,500.000 with Intl's digits showed 1,500,000 — a 1000× error. So the console
 * formats with billing's own rule (`CurrencyCodes.MinorDigits`): the codes listed below, 2 for anything else.
 *
 * The seed mirrors billing's table so the very first render is already right (a parity test pins it); the live
 * `GET …/billing/currencies` answer then replaces it ({@link applyCurrencyTable}) — billing stays the source.
 */
const ZERO_DIGIT = ['JPY', 'KRW', 'VND', 'CLP', 'ISK', 'UGX', 'XAF', 'XOF', 'PYG', 'RWF'] as const;
const THREE_DIGIT = ['KWD', 'BHD', 'OMR', 'JOD', 'TND', 'IQD', 'LYD'] as const;

/** Billing's default for a code it does not list. */
export const DEFAULT_MINOR_DIGITS = 2;

function seed(): Map<string, number> {
  const table = new Map<string, number>();
  for (const code of ZERO_DIGIT) table.set(code, 0);
  for (const code of THREE_DIGIT) table.set(code, 3);
  return table;
}

let digitsByCode: Map<string, number> = seed();

/** The digits for an ISO code: billing's table, else billing's default (2). */
export function currencyDigits(currency: string | null | undefined): number {
  if (!currency) return DEFAULT_MINOR_DIGITS;
  return digitsByCode.get(currency.trim().toUpperCase()) ?? DEFAULT_MINOR_DIGITS;
}

/** Replaces the table with billing's live answer (entries that are not a sane 0–6 are ignored). */
export function applyCurrencyTable(table: CurrencyTable | null | undefined): void {
  if (!table?.currencies?.length) return;
  const next = seed();
  for (const { code, minor_digits: digits } of table.currencies) {
    if (typeof code !== 'string' || !/^[A-Za-z]{3}$/.test(code)) continue;
    if (!Number.isInteger(digits) || digits < 0 || digits > 6) continue;
    next.set(code.toUpperCase(), digits);
  }
  digitsByCode = next;
}

/** Back to the seed (tests). */
export function resetCurrencyTable(): void {
  digitsByCode = seed();
}

/**
 * Loads billing's currency table for an environment once per session (reference data, not a money figure — caching
 * it is fine under CP2) and applies it. A role without billing:read, or billing down, keeps the seed — which is
 * billing's table as shipped.
 */
export function useCurrencyDigits(env: string | null | undefined): void {
  const allowed = hasPermission(PERMISSIONS.billingRead);
  const query = useQuery({
    queryKey: ['ops', 'billing', env, 'currencies'],
    queryFn: () => billingApi.currencies(env!),
    enabled: !!env && allowed,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  useEffect(() => {
    if (query.data) applyCurrencyTable(query.data);
  }, [query.data]);
}
